import EntityDialog from '../../EntityDialog';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { editOrganization, useOrganizationById } from '../../hooks/useOrganizations';
import { showToastError, showToastSuccess } from '../../components/ToastNotifications';
import type { OrganizationRecord } from '../../types/index';
import { getErrorMessage } from '../../utils/requestErrors';

export default function EditOrganizationContainer() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const orgId = String(params.orgId || '');
  const routeOrganization = (
    location.state as { organization?: OrganizationRecord } | null
  )?.organization;
  const routeMatch = String(routeOrganization?.id ?? '') === orgId ? routeOrganization : null;
  const organizationRequest = useOrganizationById(orgId, Boolean(orgId) && !routeMatch);
  const organization = routeMatch || organizationRequest.data;

  if (organizationRequest.isLoading) return <div data-testid="edit-organization-loading" />;

  if (organizationRequest.error) {
    return (
      <div className="card" role="alert" data-testid="edit-organization-load-error">
        <div>Organization could not be loaded.</div>
        <button
          type="button"
          className="button secondary"
          onClick={() => organizationRequest.refetch()}
          data-testid="edit-organization-retry"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!organization) {
    return <Navigate to="/organizations" replace />;
  }

  return (
    <EntityDialog
      title="Edit Organization"
      contextLabel="Organizations"
      fields={[{ name: 'name', label: 'Organization Name', required: true }]}
      initialValues={{ name: organization.name }}
      onClose={() => navigate('/organizations', { replace: true })}
      onSave={async (payload) => {
        try {
          await editOrganization(organization.id, payload.name);
          showToastSuccess('Organization successfully saved.');
          navigate('/organizations', { replace: true });
        } catch (error) {
          showToastError(getErrorMessage(error, 'Organization could not be saved.'));
          console.error(error);
        }
      }}
    />
  );
}
